import { Alert, Button, Group, Modal, Paper, Stack, Text, Textarea, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CtcLakhsInput } from "../../components/CtcLakhsInput";
import { RaiseDisputeModal } from "../../components/RaiseDisputeModal";
import { ResumeLink } from "../../components/ResumeLink";
import { StageHistory, StageProgressRail, SubmissionDecisionBanners } from "../../components/SubmissionPipeline";
import { StatusBadge } from "../../components/StatusBadge";
import { ContentCard, LoadingOverlay } from "../../components/ui";
import {
  EXPLAIN_WITH_AI_EMPTY,
  EXPLAIN_WITH_AI_FAILED,
  EXPLAIN_WITH_AI_LABEL,
  EXPLAIN_WITH_AI_LOADING,
  FIT_EXPLANATION_TITLE,
} from "../../constants/matching";
import { jobsApi, submissionsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { formatCities, formatPaise, RECRUITER_PITCH_LABEL, RECRUITER_SUBMISSION_SUBTITLE } from "../../lib/format";
import { lakhsToPaise, paiseToLakhs, validateCtcLakhs } from "../../lib/ctcLakhs";
import { axioTokens } from "../../theme";

export function HrSubmissionDetailPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [hireNotes, setHireNotes] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [offeredLakhs, setOfferedLakhs] = useState<number | string>("");
  const [disputeOpen, setDisputeOpen] = useState(false);

  const submissionQuery = useQuery({
    queryKey: ["submission", submissionId],
    queryFn: () => submissionsApi.get(submissionId!),
    enabled: !!submissionId,
  });

  const jobQuery = useQuery({
    queryKey: ["job", submissionQuery.data?.job_id],
    queryFn: () => jobsApi.get(submissionQuery.data!.job_id),
    enabled: !!submissionQuery.data?.job_id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
    queryClient.invalidateQueries({ queryKey: ["hr-submissions"] });
  };

  const job = jobQuery.data;
  const isPctCtc = job?.fee_basis === "pct_ctc";
  const offeredLakhsValid = !isPctCtc || validateCtcLakhs(offeredLakhs) == null;
  const offeredPaise =
    isPctCtc && offeredLakhsValid ? lakhsToPaise(Number(offeredLakhs)) : null;
  const ctcInBand = useMemo(() => {
    if (!isPctCtc || offeredPaise == null || Number.isNaN(offeredPaise) || offeredPaise <= 0) {
      return false;
    }
    if (job?.ctc_min_paise != null && offeredPaise < job.ctc_min_paise) return false;
    if (job?.ctc_max_paise != null && offeredPaise > job.ctc_max_paise) return false;
    return true;
  }, [isPctCtc, offeredPaise, job?.ctc_min_paise, job?.ctc_max_paise]);
  const feePreview =
    isPctCtc && offeredPaise != null && ctcInBand
      ? Math.floor((offeredPaise * (job?.fee_pct_ctc ?? 0)) / 100)
      : null;
  const hireValid =
    Boolean(job) && Boolean(joiningDate) && offeredLakhsValid && (!isPctCtc || ctcInBand);

  const hireMutation = useMutation({
    mutationFn: () =>
      submissionsApi.confirmHire(submissionId!, {
        joining_date: joiningDate,
        offered_ctc_paise: isPctCtc && offeredPaise != null ? offeredPaise : undefined,
        notes: hireNotes || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Hire confirmed", message: "Placement created." });
      setHireOpen(false);
      invalidate();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      submissionsApi.reject(submissionId!, { reason: rejectReason, notes: undefined }),
    onSuccess: () => {
      notifications.show({ color: "red", title: "Rejected", message: "Candidate rejected." });
      setRejectOpen(false);
      invalidate();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const explainMutation = useMutation({
    mutationFn: () => submissionsApi.rationale(submissionId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
    },
  });

  if (submissionQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const submission = submissionQuery.data;
  if (!submission) return <Text c="dimmed">Submission not found.</Text>;

  const pendingReview = submission.status === "pending_hr_review";
  const canReject = submission.status === "in_progress" || pendingReview;
  const canDispute = submission.status !== "withdrawn";
  const generatedExplanation = explainMutation.data?.rationale?.trim() || null;
  const cachedExplanation = submission.fit_rationale?.trim() || null;
  const fitExplanation = generatedExplanation ?? cachedExplanation;
  const explanationEmpty = explainMutation.isSuccess && !fitExplanation;
  const bandMinLakhs = job?.ctc_min_paise != null ? paiseToLakhs(job.ctc_min_paise) : null;
  const bandMaxLakhs = job?.ctc_max_paise != null ? paiseToLakhs(job.ctc_max_paise) : null;
  const bandHint =
    bandMinLakhs != null || bandMaxLakhs != null
      ? `Job CTC band: ${bandMinLakhs ?? "?"}–${bandMaxLakhs ?? "?"} L`
      : "Enter offered CTC in lakhs";

  return (
    <Stack gap={axioTokens.unit[32]}>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>
            <Text component={Link} to={`/hr/candidates/${submission.candidate.id}`} inherit>
              {submission.candidate.full_name}
            </Text>
          </Title>
          <Text c="dimmed">
            {job?.title ?? "…"} · {RECRUITER_SUBMISSION_SUBTITLE}
          </Text>
        </div>
        <Group>
          {canDispute && (
            <Button variant="light" color="orange" size="xs" onClick={() => setDisputeOpen(true)}>
              Raise a dispute
            </Button>
          )}
          <StatusBadge status={submission.status} />
        </Group>
      </Group>

      {pendingReview && (
        <Alert color="orange" title="Ready for hire confirmation">
          The recruiter has completed all stages and submitted this candidate for your review. Confirm the hire
          to create a placement.
        </Alert>
      )}

      <SubmissionDecisionBanners
        status={submission.status}
        rejectionReason={submission.rejection_reason}
        history={submission.history}
      />

      {pendingReview && (
        <Paper withBorder p="md">
          <Group>
            <Button color="green" size="md" onClick={() => setHireOpen(true)} data-testid="confirm-hire">
              Confirm hire
            </Button>
            <Button color="red" variant="light" onClick={() => setRejectOpen(true)} data-testid="reject-submission">
              Reject
            </Button>
          </Group>
        </Paper>
      )}

      {canReject && !pendingReview && (
        <Group>
          <Button color="red" variant="light" onClick={() => setRejectOpen(true)}>
            Reject candidate
          </Button>
        </Group>
      )}

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Hiring Flow Progress
        </Title>
        <StageProgressRail
          stages={submission.stages}
          currentStageId={submission.current_stage?.id ?? null}
          status={submission.status}
          history={submission.history}
        />
      </Paper>

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Candidate Details
        </Title>
        <Group gap="var(--axio-unit-40)">
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Phone
            </Text>
            <Text className="tabular">{submission.candidate.phone}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Email
            </Text>
            <Text>{submission.candidate.email ?? "—"}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              City
            </Text>
            <Text>{formatCities(submission.candidate.cities)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Resume
            </Text>
            <ResumeLink url={submission.candidate.resume_url} variant="link" />
          </div>
        </Group>
        {submission.pitch_note && (
          <>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)" mt="md">
              {RECRUITER_PITCH_LABEL}
            </Text>
            <Text>{submission.pitch_note}</Text>
          </>
        )}
      </Paper>

      <ContentCard>
        <Stack gap="md">
          <Title order={5}>{FIT_EXPLANATION_TITLE}</Title>
          {explainMutation.isPending ? (
            <Text size="sm" c="dimmed">
              {EXPLAIN_WITH_AI_LOADING}
            </Text>
          ) : null}
          {explainMutation.isError ? (
            <Text size="sm" c="dimmed">
              {EXPLAIN_WITH_AI_FAILED}
            </Text>
          ) : null}
          {explanationEmpty && !explainMutation.isPending ? (
            <Text size="sm" c="dimmed">
              {EXPLAIN_WITH_AI_EMPTY}
            </Text>
          ) : null}
          {fitExplanation && !explainMutation.isPending ? (
            <Text size="sm">{fitExplanation}</Text>
          ) : null}
          {!fitExplanation && !explanationEmpty && !explainMutation.isPending && !explainMutation.isError ? (
            <Button
              size="xs"
              variant="default"
              w="fit-content"
              onClick={() => explainMutation.mutate()}
            >
              {EXPLAIN_WITH_AI_LABEL}
            </Button>
          ) : null}
        </Stack>
      </ContentCard>

      <StageHistory history={submission.history} stages={submission.stages} />

      <Button variant="subtle" component={Link} to="/hr/submissions">
        ← Back to submissions
      </Button>

      <Modal
        opened={hireOpen}
        onClose={() => setHireOpen(false)}
        title="Confirm hire"
        centered
      >
        <Stack>
          <TextInput
            type="date"
            label="Joining date"
            required
            value={joiningDate}
            onChange={(e) => setJoiningDate(e.currentTarget.value)}
            data-testid="hire-joining-date"
          />
          {isPctCtc && (
            <>
              <CtcLakhsInput
                label="Offered CTC (lakhs / year)"
                required
                value={offeredLakhs}
                onChange={setOfferedLakhs}
                extraDescription={bandHint}
                error={
                  offeredLakhs !== "" && offeredLakhs != null
                    ? validateCtcLakhs(offeredLakhs)
                    : undefined
                }
                data-testid="hire-offered-ctc"
              />
              {feePreview != null && (
                <Text size="sm" c="dimmed">
                  Placement fee preview: {formatPaise(feePreview)}
                  {job?.fee_pct_ctc != null ? ` (${job.fee_pct_ctc}% of offered CTC)` : ""}
                </Text>
              )}
            </>
          )}
          <Textarea
            label="Notes (optional)"
            placeholder="Offer notes..."
            value={hireNotes}
            onChange={(e) => setHireNotes(e.currentTarget.value)}
          />
          <Button
            color="green"
            loading={hireMutation.isPending}
            disabled={!hireValid}
            onClick={() => hireMutation.mutate()}
            data-testid="hire-confirm-submit"
          >
            Confirm hire
          </Button>
        </Stack>
      </Modal>

      <Modal opened={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject candidate" centered>
        <Stack>
          <Textarea
            label="Reason"
            required
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={2}
          />
          <Button
            color="red"
            loading={rejectMutation.isPending}
            disabled={rejectReason.trim().length < 2}
            onClick={() => rejectMutation.mutate()}
          >
            Confirm reject
          </Button>
        </Stack>
      </Modal>

      <RaiseDisputeModal
        opened={disputeOpen}
        onClose={() => setDisputeOpen(false)}
        submissionId={submission.id}
        defaultType={submission.status === "hired" ? "no_join" : "quality"}
        onCreated={(id) => navigate(`/hr/disputes/${id}`)}
      />
    </Stack>
  );
}
